/**
 * GestureDraw — draw in the air with your hand, rendered as a neon overlay.
 *
 * Reusable, source-agnostic module. Give it a <video> (any camera stream)
 * and an overlay <canvas>; it runs MediaPipe HandLandmarker (via
 * window.GestureHands), turns a pinch gesture into pen-down, smooths the
 * fingertip with a 1€ filter, and paints neon strokes.
 *
 * Designed to be embedded later as an overlay layer in webcam-recorder /
 * screen-recorder (call `renderInto(ctx, w, h)` from their compositor),
 * so all coordinates are stored NORMALIZED [0..1] — resolution-independent.
 *
 * UX affordances (so the user always knows where the pen is and whether it
 * is down): a live hand skeleton overlay + a fingertip cursor whose ring
 * shrinks as the pinch closes and snaps to a filled, glowing dot when the
 * pen goes down. Accidental single-frame taps are suppressed.
 *
 * Public API (window.GestureDraw.create(opts) -> instance):
 *   start() / stop()         — begin / end the detection loop + camera use
 *   clear() / undo()         — drawing edits
 *   setColor(css) / setBrush(px) / setMode('pinch'|'point'|'erase')
 *   setSkeleton(bool)        — toggle the hand-skeleton overlay
 *   getStrokes()             — [{color,width,points:[{x,y}]}] normalized
 *   renderInto(ctx, w, h)    — paint the whole drawing (strokes only) onto a 2D context
 *   isDrawing()              — pen currently down
 *   destroy()                — stop + release everything
 *   opts: { video, canvas, hands?, mirror=true, mode='pinch',
 *           color='#34e1eb', width=6, numHands=1, drawCursor=true,
 *           showSkeleton=true, eraseRadius=0.05,
 *           filter?:{minCutoff,beta,dCutoff},
 *           onready, onerror, onhand(bool), ongesture('down'|'up'), telemetry }
 *
 * Exposed for tests: window.GestureDraw.OneEuro, .classifyGesture, .neonStroke.
 */
(function () {
    'use strict';

    /* ── 1€ filter (Casiez et al.) — kills MediaPipe landmark jitter ─────────── */
    function LowPass() { this.init = false; this.prev = 0; this.a = 1; }
    LowPass.prototype.setAlpha = function (a) { this.a = a; };
    LowPass.prototype.filter = function (x) {
        var y = this.init ? (this.a * x + (1 - this.a) * this.prev) : x;
        this.prev = y; this.init = true; return y;
    };

    function OneEuro(o) {
        o = o || {};
        this.freq = o.freq || 30;
        // Tuned for a fingertip-drawing cursor. minCutoff keeps the baseline lag
        // low so the cursor dot stays glued to the fingertip; a modest beta lets
        // it track fast flicks without much lag while damping the mid-motion
        // tremor that an air-drawn line picks up (the actual LINE is smoothed
        // further by chaikin() at render time, decoupled from the cursor).
        // ⚠️ Feel levers — tune on a real camera: ↑minCutoff = snappier/jumpier,
        // ↓beta = smoother line but laggier on fast motion.
        this.minCutoff = o.minCutoff != null ? o.minCutoff : 2.0;
        this.beta = o.beta != null ? o.beta : 0.4;
        this.dCutoff = o.dCutoff != null ? o.dCutoff : 1.0;
        this.x = new LowPass(); this.dx = new LowPass();
        this.lastT = null;
    }
    OneEuro.prototype._alpha = function (cutoff) {
        var te = 1 / this.freq, tau = 1 / (2 * Math.PI * cutoff);
        return 1 / (1 + tau / te);
    };
    // timestamp in SECONDS
    OneEuro.prototype.filter = function (v, t) {
        if (this.lastT != null && t != null && t > this.lastT) this.freq = 1 / (t - this.lastT);
        this.lastT = t;
        var prev = this.x.init ? this.x.prev : v;
        var dv = (v - prev) * this.freq;
        this.dx.setAlpha(this._alpha(this.dCutoff));
        var edv = this.dx.filter(dv);
        var cutoff = this.minCutoff + this.beta * Math.abs(edv);
        this.x.setAlpha(this._alpha(cutoff));
        return this.x.filter(v);
    };
    OneEuro.prototype.reset = function () {
        this.x = new LowPass(); this.dx = new LowPass(); this.lastT = null;
    };

    /* ── gesture classification from 21 landmarks ────────────────────────────── */
    function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }

    // Returns { pinchRatio, pointing, pinchTip, indexTip } in normalized space.
    // pinchRatio = thumb-tip↔index-tip distance / hand size (wrist→middle-MCP),
    // so it is scale/distance-independent. `pointing` = index extended while the
    // other three fingers are folded (orientation-independent, same trick as RPS).
    function classifyGesture(lm) {
        var handSize = dist(lm[0], lm[9]) || 0.0001;
        var pinchRatio = dist(lm[4], lm[8]) / handSize;
        function ext(tip, pip) { return dist(lm[tip], lm[0]) > dist(lm[pip], lm[0]) * 1.15; }
        var idx = ext(8, 6), mid = ext(12, 10), rng = ext(16, 14), pky = ext(20, 18);
        var pointing = idx && !mid && !rng && !pky;
        return {
            pinchRatio: pinchRatio,
            pointing: pointing,
            pinchTip: { x: (lm[4].x + lm[8].x) / 2, y: (lm[4].y + lm[8].y) / 2 },
            indexTip: { x: lm[8].x, y: lm[8].y },
        };
    }

    // Pinch hysteresis: cross DOWN below 0.34, release UP above 0.55 → no flicker.
    var PINCH_DOWN = 0.34, PINCH_UP = 0.55, PINCH_OPEN = 0.9;

    // Stroke-continuity guards (kill the "the line keeps detaching from my
    // finger" complaint):
    //  • POINT_LIFT_FRAMES — in point mode the "index extended" pose flickers
    //    frame-to-frame during motion; hold the pen down through a few bad
    //    frames so one misread doesn't chop the stroke in two. (Pinch/erase
    //    already debounce via the ratio hysteresis, so they lift instantly.)
    //  • MISS_GRACE — keep an active stroke alive across a few frames where the
    //    detector momentarily loses the hand (motion blur / dropped frame),
    //    instead of ending the stroke on the very first miss.
    var POINT_LIFT_FRAMES = 3, MISS_GRACE = 4;

    // MediaPipe hand skeleton bone pairs (21-landmark model).
    var HAND_BONES = [
        [0, 1], [1, 2], [2, 3], [3, 4],        // thumb
        [0, 5], [5, 6], [6, 7], [7, 8],        // index
        [5, 9], [9, 10], [10, 11], [11, 12],   // middle
        [9, 13], [13, 14], [14, 15], [15, 16], // ring
        [13, 17], [17, 18], [18, 19], [19, 20],// pinky
        [0, 17],                               // palm base
    ];

    /* ── neon rendering ──────────────────────────────────────────────────────── */

    // Chaikin corner-cutting: replaces every segment with points at 1/4 and 3/4,
    // rounding off the sharp kinks that a hand drawing in the air (no surface to
    // steady on) inevitably produces. Run on the RENDER path only — the stored
    // points and the live cursor stay raw/responsive, so the dot still tracks the
    // fingertip while the painted LINE comes out smooth. Endpoints are preserved.
    function chaikin(pts, iters) {
        var p = pts;
        for (var it = 0; it < iters && p.length >= 3; it++) {
            var out = [p[0]];
            for (var i = 0; i < p.length - 1; i++) {
                var a = p[i], b = p[i + 1];
                out.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
                out.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
            }
            out.push(p[p.length - 1]);
            p = out;
        }
        return p;
    }

    function tracePath(ctx, pts) {
        ctx.beginPath();
        if (pts.length === 1) { return; }
        ctx.moveTo(pts[0].x, pts[0].y);
        for (var i = 1; i < pts.length - 1; i++) {
            var mx = (pts[i].x + pts[i + 1].x) / 2, my = (pts[i].y + pts[i + 1].y) / 2;
            ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
        }
        var last = pts[pts.length - 1];
        ctx.lineTo(last.x, last.y);
    }

    // pts in CANVAS px. Glow (blurred colour) + bright core = neon look.
    function neonStroke(ctx, pts, color, width) {
        if (!pts || !pts.length) return;
        if (pts.length >= 3) pts = chaikin(pts, 2);
        ctx.save();
        ctx.lineJoin = 'round'; ctx.lineCap = 'round';
        if (pts.length === 1) {
            ctx.shadowColor = color; ctx.shadowBlur = Math.max(8, width * 2.5);
            ctx.fillStyle = color;
            ctx.beginPath(); ctx.arc(pts[0].x, pts[0].y, width / 2, 0, 2 * Math.PI); ctx.fill(); ctx.fill();
            ctx.restore(); return;
        }
        // glow pass (twice → intensify)
        ctx.shadowColor = color; ctx.shadowBlur = Math.max(8, width * 2.5);
        ctx.strokeStyle = color; ctx.lineWidth = width;
        tracePath(ctx, pts); ctx.stroke(); ctx.stroke();
        // bright core
        ctx.shadowBlur = Math.max(4, width * 1.0);
        ctx.strokeStyle = '#ffffff'; ctx.globalAlpha = 0.85;
        ctx.lineWidth = Math.max(1, width * 0.4);
        tracePath(ctx, pts); ctx.stroke();
        ctx.restore();
    }

    /* ── instance ────────────────────────────────────────────────────────────── */
    function GestureDrawInstance(opts) {
        opts = opts || {};
        this.video = opts.video;
        this.canvas = opts.canvas;
        this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
        this.hands = opts.hands || window.GestureHands;
        this.mirror = opts.mirror !== false;
        this.mode = opts.mode || 'pinch';
        this.color = opts.color || '#34e1eb';
        this.width = opts.width || 6;
        this.numHands = opts.numHands || 1;
        this.showCursor = opts.drawCursor !== false;
        this.showSkeleton = opts.showSkeleton !== false;
        this.eraseRadius = opts.eraseRadius || 0.05;
        this.filterOpts = opts.filter || {};
        // coordMap: optional (x,y)->{x,y} transform from raw camera-normalized
        // coords into the OUTPUT space (e.g. webcam-recorder's aspect-crop + the
        // recording mirror). When set it fully owns the mirror; when absent the
        // built-in `mirror` flag does the selfie flip (air-draw standalone).
        this.coordMap = (typeof opts.coordMap === 'function') ? opts.coordMap : null;
        // renderMode 'full' (default) paints strokes+skeleton+cursor on our own
        // canvas. 'aidsOnly' paints ONLY skeleton+cursor (strokes are baked
        // elsewhere via renderInto — used when overlaying onto a host canvas).
        this.renderMode = opts.renderMode === 'aidsOnly' ? 'aidsOnly' : 'full';
        this.cb = {
            ready: opts.onready || function () {},
            error: opts.onerror || function () {},
            hand: opts.onhand || function () {},
            gesture: opts.ongesture || function () {},
        };
        this.tel = opts.telemetry || function () {};

        this.landmarker = null;
        this.strokes = [];          // completed strokes (normalized points)
        this.current = null;        // in-progress stroke
        this.drawing = false;
        this.cursor = null;         // {x,y} normalized (smoothed)
        this.pinchRatio = PINCH_OPEN;
        this._screenLm = null;      // mirrored normalized landmarks for viz
        this.handPresent = false;
        this._upStreak = 0;         // consecutive "pen up" frames while drawing
        this._missStreak = 0;       // consecutive "no hand" frames while drawing
        this._raf = 0;
        this._running = false;
        this._fx = new OneEuro(this.filterOpts);
        this._fy = new OneEuro(this.filterOpts);
        this.commit = null;         // offscreen px canvas of completed strokes
        this._commitCtx = null;
        this._loop = this._loop.bind(this);
        this._onResize = this._onResize.bind(this);
    }

    GestureDrawInstance.prototype.start = function () {
        var self = this;
        if (this._running) return Promise.resolve();
        this._syncSize();
        window.addEventListener('resize', this._onResize);
        return this.hands.load({
            numHands: this.numHands,
            // Lower presence/tracking thresholds keep the hand "locked" through
            // motion blur and partial occlusion, so fast strokes don't drop the
            // hand mid-draw; detection stays at the default for a confident
            // initial lock-on.
            minHandDetectionConfidence: 0.4,
            minHandPresenceConfidence: 0.3,
            minTrackingConfidence: 0.3,
        }).then(function (l) {
            self.landmarker = l;
            self._running = true;
            self.cb.ready();
            self.tel('ready', {});
            self._raf = requestAnimationFrame(self._loop);
        }).catch(function (e) {
            self.cb.error(e);
            self.tel('model_failed', { msg: String(e && e.message || e) });
            throw e;
        });
    };

    GestureDrawInstance.prototype.stop = function () {
        this._running = false;
        if (this._raf) { cancelAnimationFrame(this._raf); this._raf = 0; }
        window.removeEventListener('resize', this._onResize);
        this._endStroke();
        this._screenLm = null; this.cursor = null;
    };

    GestureDrawInstance.prototype.destroy = function () {
        this.stop();
        this.strokes = []; this.current = null; this.commit = null; this._commitCtx = null;
    };

    GestureDrawInstance.prototype._syncSize = function () {
        if (!this.canvas) return;
        var w = this.canvas.clientWidth || this.canvas.width || 640;
        var h = this.canvas.clientHeight || this.canvas.height || 480;
        if (this.canvas.width !== w) this.canvas.width = w;
        if (this.canvas.height !== h) this.canvas.height = h;
        this.commit = document.createElement('canvas');
        this.commit.width = w; this.commit.height = h;
        this._commitCtx = this.commit.getContext('2d');
        this._rebuildCommit();
    };

    GestureDrawInstance.prototype._onResize = function () { this._syncSize(); };

    GestureDrawInstance.prototype._rebuildCommit = function () {
        if (!this._commitCtx) return;
        var w = this.commit.width, h = this.commit.height;
        this._commitCtx.clearRect(0, 0, w, h);
        for (var i = 0; i < this.strokes.length; i++) {
            neonStroke(this._commitCtx, this._denorm(this.strokes[i].points, w, h),
                this.strokes[i].color, this.strokes[i].width);
        }
    };

    GestureDrawInstance.prototype._denorm = function (pts, w, h) {
        var out = [];
        for (var i = 0; i < pts.length; i++) out.push({ x: pts[i].x * w, y: pts[i].y * h });
        return out;
    };

    GestureDrawInstance.prototype._loop = function (now) {
        if (!this._running) return;
        this._raf = requestAnimationFrame(this._loop);
        var v = this.video;
        if (!v || !v.videoWidth) return;
        // MediaPipe VIDEO mode requires STRICTLY increasing timestamps; rAF `now`
        // can repeat or step back by a fraction on high-refresh displays / under
        // load, which makes detectForVideo drop that frame with a "Packet
        // timestamp mismatch on stream norm_rect" graph error. Force monotonicity.
        // The counter lives ON THE LANDMARKER (GestureHands caches a single shared
        // instance) so concurrent gd instances can't feed it crossing timestamps.
        var L = this.landmarker, ts = now;
        if (L.__gdLastTs != null && ts <= L.__gdLastTs) ts = L.__gdLastTs + 1;
        L.__gdLastTs = ts;
        var res = null;
        try { res = L.detectForVideo(v, ts); } catch (e) { return; }
        var lm = (res && res.landmarks && res.landmarks[0]) || null;
        this._onFrame(lm, now);
        this._render();
    };

    // Map a raw camera-normalized point into output/screen normalized space.
    GestureDrawInstance.prototype._map = function (x, y) {
        if (this.coordMap) { var p = this.coordMap(x, y); return { x: p.x, y: p.y }; }
        return { x: this.mirror ? (1 - x) : x, y: y };
    };

    // Core per-frame state machine. Public so tests can drive it with scripted
    // landmark arrays (no real camera / motion needed — headless can't feed that).
    GestureDrawInstance.prototype._onFrame = function (lm, nowMs) {
        var tSec = (nowMs != null ? nowMs : 0) / 1000;
        if (!lm) {
            // Hand momentarily lost mid-stroke (motion blur / dropped detection):
            // hold the stroke open for a few frames instead of chopping it on the
            // first miss. The cursor/skeleton freeze at their last position.
            if (this.drawing && this._missStreak < MISS_GRACE) { this._missStreak++; return; }
            if (this.handPresent) { this.handPresent = false; this.cb.hand(false); }
            this.cursor = null; this._screenLm = null;
            this._endStroke();
            return;
        }
        this._missStreak = 0;
        if (!this.handPresent) { this.handPresent = true; this.cb.hand(true); }

        // Map raw landmarks into output/screen space for the skeleton + cursor.
        var sl = new Array(lm.length);
        for (var k = 0; k < lm.length; k++) sl[k] = this._map(lm[k].x, lm[k].y);
        this._screenLm = sl;

        var g = classifyGesture(lm);
        this.pinchRatio = g.pinchRatio;
        var raw = this.mode === 'point' ? g.indexTip : g.pinchTip;
        var m = this._map(raw.x, raw.y);
        var fx = this._fx.filter(m.x, tSec);
        var fy = this._fy.filter(m.y, tSec);
        this.cursor = { x: fx, y: fy };

        var penDown;
        if (this.mode === 'point') penDown = g.pointing;
        else penDown = this.drawing ? (g.pinchRatio < PINCH_UP) : (g.pinchRatio < PINCH_DOWN); // pinch + erase

        // Pen-up debounce: a single mis-read frame must not chop the stroke.
        // Any genuine "down" frame resets the streak; we only actually lift once
        // we've seen `liftAfter` consecutive "up" frames. Pinch/erase already
        // debounce via the ratio hysteresis → liftAfter=1 (instant release).
        if (penDown) { this._upStreak = 0; }
        else if (this.drawing) {
            this._upStreak++;
            var liftAfter = (this.mode === 'point') ? POINT_LIFT_FRAMES : 1;
            if (this._upStreak < liftAfter) penDown = true; // bridge the flicker — keep drawing
        }

        if (this.mode === 'erase') {
            if (penDown) this._eraseAt(fx, fy);
            if (penDown !== this.drawing) this.cb.gesture(penDown ? 'down' : 'up');
            this.drawing = penDown;
            return;
        }

        if (penDown) {
            if (!this.drawing) {
                this.drawing = true;
                this.current = { color: this.color, width: this.width, points: [{ x: fx, y: fy }], frames: 1 };
                this.cb.gesture('down');
                this.tel('stroke_start', { mode: this.mode });
            } else {
                this.current.frames++;
                var pts = this.current.points, last = pts[pts.length - 1];
                if (Math.hypot(fx - last.x, fy - last.y) > 0.004) pts.push({ x: fx, y: fy });
            }
        } else if (this.drawing) {
            this._endStroke();
        }
    };

    // Drop accidental flicks: a stroke must have ≥2 points (real movement) and
    // span a minimum distance — otherwise it is a one-frame tap that just litters
    // the canvas with dots (the chaos seen in point mode).
    GestureDrawInstance.prototype._endStroke = function () {
        if (this.drawing && this.current) {
            var c = this.current, keep = c.points.length >= 2;
            if (keep) {
                var minX = 1, minY = 1, maxX = 0, maxY = 0;
                for (var i = 0; i < c.points.length; i++) {
                    var p = c.points[i];
                    if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
                    if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
                }
                if (Math.hypot(maxX - minX, maxY - minY) < 0.01 && c.points.length < 4) keep = false;
            }
            if (keep) {
                this.strokes.push(c);
                if (this._commitCtx) neonStroke(this._commitCtx, this._denorm(c.points, this.commit.width, this.commit.height), c.color, c.width);
                this.tel('stroke_end', { points: c.points.length });
            }
            this.cb.gesture('up');
        }
        this.drawing = false;
        this.current = null;
        this._upStreak = 0;
        this._missStreak = 0;
    };

    GestureDrawInstance.prototype._eraseAt = function (x, y) {
        var r = this.eraseRadius, removed = false;
        for (var i = this.strokes.length - 1; i >= 0; i--) {
            var pts = this.strokes[i].points;
            for (var j = 0; j < pts.length; j++) {
                if (Math.hypot(pts[j].x - x, pts[j].y - y) < r) { this.strokes.splice(i, 1); removed = true; break; }
            }
        }
        if (removed) { this._rebuildCommit(); this.tel('erase', { left: this.strokes.length }); }
    };

    GestureDrawInstance.prototype._render = function () {
        var ctx = this.ctx; if (!ctx) return;
        var w = this.canvas.width, h = this.canvas.height;
        ctx.clearRect(0, 0, w, h);
        // In 'aidsOnly' mode the strokes are baked onto a host canvas via
        // renderInto() — here we paint only the live aids (skeleton + cursor).
        if (this.renderMode !== 'aidsOnly') {
            if (this.commit) ctx.drawImage(this.commit, 0, 0, w, h);
            if (this.current && this.current.points.length) {
                neonStroke(ctx, this._denorm(this.current.points, w, h), this.current.color, this.current.width);
            }
        }
        if (this.showSkeleton && this._screenLm) this._drawSkeleton(ctx, w, h);
        if (this.showCursor && this.cursor) this._drawCursor(ctx, w, h);
    };

    GestureDrawInstance.prototype._drawSkeleton = function (ctx, w, h) {
        var lm = this._screenLm; if (!lm || lm.length < 21) return;
        ctx.save();
        ctx.lineCap = 'round'; ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(255,255,255,0.25)';
        for (var i = 0; i < HAND_BONES.length; i++) {
            var a = lm[HAND_BONES[i][0]], b = lm[HAND_BONES[i][1]];
            ctx.beginPath(); ctx.moveTo(a.x * w, a.y * h); ctx.lineTo(b.x * w, b.y * h); ctx.stroke();
        }
        ctx.fillStyle = 'rgba(255,255,255,0.45)';
        for (var k = 0; k < 21; k++) {
            ctx.beginPath(); ctx.arc(lm[k].x * w, lm[k].y * h, 2.5, 0, 6.2832); ctx.fill();
        }
        ctx.restore();
    };

    // The fingertip cursor — the user's anchor for "where am I and is the pen down".
    GestureDrawInstance.prototype._drawCursor = function (ctx, w, h) {
        var lm = this._screenLm;
        var cx = this.cursor.x * w, cy = this.cursor.y * h;
        var erase = (this.mode === 'erase');
        var main = erase ? '#ff5c5c' : this.color;
        ctx.save();
        ctx.lineCap = 'round';

        // pinch/erase: show the two fingertips converging — teaches the gesture
        if ((this.mode === 'pinch' || erase) && lm && lm.length >= 9) {
            var tx = lm[4].x * w, ty = lm[4].y * h, ix = lm[8].x * w, iy = lm[8].y * h;
            ctx.strokeStyle = 'rgba(255,255,255,0.45)';
            ctx.setLineDash([4, 4]); ctx.lineWidth = 1.5;
            ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(ix, iy); ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = 'rgba(255,255,255,0.8)';
            ctx.beginPath(); ctx.arc(tx, ty, 4, 0, 6.2832); ctx.fill();
            ctx.beginPath(); ctx.arc(ix, iy, 4, 0, 6.2832); ctx.fill();
        }

        if (this.drawing) {
            // active pen: filled glowing dot + soft pulse ring
            ctx.shadowColor = main; ctx.shadowBlur = 18;
            ctx.fillStyle = main;
            ctx.beginPath(); ctx.arc(cx, cy, Math.max(5, this.width * 0.6), 0, 6.2832); ctx.fill();
            ctx.shadowBlur = 0; ctx.globalAlpha = 0.55;
            ctx.strokeStyle = main; ctx.lineWidth = 2;
            ctx.beginPath(); ctx.arc(cx, cy, Math.max(13, this.width * 1.5), 0, 6.2832); ctx.stroke();
        } else {
            // idle reticle: ring radius reflects how open the pinch is (snaps small as you pinch)
            var o = 1;
            if (this.mode === 'pinch' || erase) {
                o = Math.max(0, Math.min(1, (this.pinchRatio - PINCH_DOWN) / (PINCH_OPEN - PINCH_DOWN)));
            }
            var r = 9 + o * 13;
            var ringCol = erase ? 'rgba(255,92,92,0.95)' : 'rgba(255,255,255,0.95)';
            ctx.shadowColor = erase ? '#ff5c5c' : 'rgba(255,255,255,0.7)'; ctx.shadowBlur = 8;
            ctx.strokeStyle = ringCol; ctx.lineWidth = 2;
            ctx.beginPath(); ctx.arc(cx, cy, r, 0, 6.2832); ctx.stroke();
            ctx.shadowBlur = 0;
            ctx.fillStyle = erase ? 'rgba(255,92,92,0.95)' : main;
            ctx.beginPath(); ctx.arc(cx, cy, 3, 0, 6.2832); ctx.fill();
        }
        ctx.restore();
    };

    /* ── public edits / accessors ────────────────────────────────────────────── */
    GestureDrawInstance.prototype.clear = function () {
        this.strokes = []; this.current = null; this.drawing = false;
        this._rebuildCommit();
        if (this.ctx) this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.tel('clear', {});
    };
    GestureDrawInstance.prototype.undo = function () {
        if (this.drawing) { this.drawing = false; this.current = null; }
        else this.strokes.pop();
        this._rebuildCommit();
        this.tel('undo', { left: this.strokes.length });
    };
    GestureDrawInstance.prototype.setCoordMap = function (fn) { this.coordMap = (typeof fn === 'function') ? fn : null; };
    GestureDrawInstance.prototype.setRenderMode = function (m) { this.renderMode = (m === 'aidsOnly') ? 'aidsOnly' : 'full'; };
    GestureDrawInstance.prototype.setColor = function (c) { this.color = c; };
    GestureDrawInstance.prototype.setBrush = function (px) { this.width = +px || this.width; };
    GestureDrawInstance.prototype.setMode = function (m) {
        if (m === 'pinch' || m === 'point' || m === 'erase') {
            this._endStroke();
            this.mode = m; this._fx.reset(); this._fy.reset();
        }
    };
    GestureDrawInstance.prototype.setSkeleton = function (on) { this.showSkeleton = !!on; };
    GestureDrawInstance.prototype.resize = function () { this._syncSize(); };
    GestureDrawInstance.prototype.getStrokes = function () { return this.strokes.slice(); };
    GestureDrawInstance.prototype.isEmpty = function () { return this.strokes.length === 0 && !(this.current && this.current.points.length); };
    GestureDrawInstance.prototype.isDrawing = function () { return this.drawing; };

    // Paint the whole drawing (completed + in-progress) onto any 2D context at
    // w×h. Strokes only — no skeleton/cursor. Used by tools that composite the
    // overlay into a recording, and by the PNG export.
    GestureDrawInstance.prototype.renderInto = function (ctx, w, h) {
        for (var i = 0; i < this.strokes.length; i++) {
            neonStroke(ctx, this._denorm(this.strokes[i].points, w, h), this.strokes[i].color, this.strokes[i].width);
        }
        if (this.current && this.current.points.length) {
            neonStroke(ctx, this._denorm(this.current.points, w, h), this.current.color, this.current.width);
        }
    };

    window.GestureDraw = {
        create: function (opts) { return new GestureDrawInstance(opts); },
        // test/introspection hooks
        OneEuro: OneEuro,
        classifyGesture: classifyGesture,
        neonStroke: neonStroke,
        PINCH_DOWN: PINCH_DOWN,
        PINCH_UP: PINCH_UP,
    };
})();
